/**
 * API Service for backend communication
 */

const API_BASE = '/api';

// Token management
let authToken: string | null = null;

export function setAuthToken(token: string | null): void {
  authToken = token;
  if (token) {
    localStorage.setItem('authToken', token);
  } else {
    localStorage.removeItem('authToken');
  }
}

export function getAuthToken(): string | null {
  if (!authToken) {
    authToken = localStorage.getItem('authToken');
  }
  return authToken;
}

async function fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
  const token = getAuthToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${url}`, {
    ...options,
    headers,
  });

  if (response.status === 401) {
    setAuthToken(null);
    window.location.href = '/login';
  }

  return response;
}

// Types
export interface SiteSummary {
  siteId: string;
  name: string;
  location: string;
  totalUtilization: number;
  totalPlannedCases: number;
  totalOpenCapacity: number;
}

export interface SiteCapabilities {
  operatingSchedule: string;
  availableFormats: string[];
  totalLines: number;
  activeLines: number;
  minimumOrderQuantity: number;
  description: string;
}

export interface CostParameters {
  totalBudget: number;
  fixedCostPercentage: number;
  fixedCost: number;
  incrementalCaseImpact: number;
  extendedValuePerMillion: number;
}

export interface Site {
  siteId: string;
  name: string;
  location: string;
  capabilities: SiteCapabilities;
  costParameters: CostParameters;
  createdAt: string;
  updatedAt: string;
}

export interface CapacityRow {
  siteId: string;
  format: string;
  plannedCases: number;
  loadCapacity: number | null;
  openCapacity: number | null;
  utilization: number;
}

export interface SiteDetail {
  site: Site;
  capacity: CapacityRow[];
  costParameters: CostParameters;
}

export interface User {
  userId: string;
  email: string;
  role: 'coo' | 'finance';
  name: string;
}

export interface LoginResponse {
  token: string;
  user: User;
  expiresIn: string;
}

export interface FormatAdjustment {
  format: string;
  baselineValue: number;
  adjustedValue: number;
  delta: number;
}

export interface ScenarioMetrics {
  totalPlannedCases: number;
  totalOpenCapacity: number;
  overallUtilization: number;
  currentCostPerCase: number;
  scenarioCostPerCase: number;
  savingsPerCase: number;
  totalExtendedValue: number;
}

export interface CapacityWarning {
  format: string;
  message: string;
  currentValue: number;
  maxValue: number;
}

export interface ScenarioResult {
  newUtilization: number;
  newCostPerCase: number;
  savingsPerCase: number;
  totalExtendedValue: number;
  warnings: CapacityWarning[];
  metrics: ScenarioMetrics;
}

// API Functions
export async function fetchSites(): Promise<SiteSummary[]> {
  const response = await fetchWithAuth('/sites');
  if (!response.ok) {
    throw new Error('Failed to fetch sites');
  }
  const data = await response.json();
  return data.sites;
}

export async function fetchSiteDetail(siteId: string): Promise<SiteDetail> {
  const response = await fetchWithAuth(`/sites/${siteId}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch site ${siteId}`);
  }
  return response.json();
}

export async function fetchSiteCapacity(siteId: string): Promise<CapacityRow[]> {
  const response = await fetchWithAuth(`/sites/${siteId}/capacity`);
  if (!response.ok) {
    throw new Error(`Failed to fetch capacity for site ${siteId}`);
  }
  const data = await response.json();
  return data.capacity;
}

export async function login(email: string, password: string): Promise<LoginResponse> {
  const response = await fetch(`${API_BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  if (!response.ok) {
    throw new Error('Login failed');
  }
  return response.json();
}

export async function getTestTokens(): Promise<{
  coo: { token: string; user: User };
  finance: { token: string; user: User };
}> {
  const response = await fetch(`${API_BASE}/auth/test-tokens`);
  if (!response.ok) {
    throw new Error('Failed to get test tokens');
  }
  return response.json();
}

export async function calculateScenario(
  siteId: string,
  adjustments: FormatAdjustment[]
): Promise<ScenarioResult> {
  const response = await fetchWithAuth('/scenarios/calculate', {
    method: 'POST',
    body: JSON.stringify({ siteId, adjustments }),
  });
  if (!response.ok) {
    throw new Error('Failed to calculate scenario');
  }
  return response.json();
}

// Insights API Types
export interface Recommendation {
  id: string;
  type: 'capacity_fill' | 'cost_optimization' | 'format_priority';
  title: string;
  description: string;
  reasoning: string;
  impact: {
    metric: string;
    currentValue: number;
    projectedValue: number;
  };
  priority: 'high' | 'medium' | 'low';
}

export interface Anomaly {
  id: string;
  format: string;
  type: 'low_utilization' | 'capacity_mismatch' | 'cost_outlier';
  description: string;
  severity: 'warning' | 'info';
  suggestedAction: string;
}

export interface InsightsResponse {
  recommendations: Recommendation[];
  anomalies: Anomaly[];
}

export interface ScenarioComparison {
  siteId: string;
  scenarioName: string;
  adjustments: FormatAdjustment[];
}

export interface Tradeoff {
  scenario: string;
  pros: string[];
  cons: string[];
  netImpact: number;
}

export interface ComparisonAnalysis {
  summary: string;
  tradeoffs: Tradeoff[];
  recommendation: string;
  reasoning: string;
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface ChatRequest {
  siteId: string;
  message: string;
  context?: {
    previousMessages: ChatMessage[];
  };
}

export interface ChatResponse {
  response: string;
  reasoning: string;
  suggestedFollowUps: string[];
}

// Insights API Functions
export async function fetchInsights(siteId: string): Promise<InsightsResponse> {
  const response = await fetchWithAuth(`/insights/${siteId}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch insights for site ${siteId}`);
  }
  return response.json();
}

export async function compareScenarios(scenarios: ScenarioComparison[]): Promise<ComparisonAnalysis> {
  const response = await fetchWithAuth('/insights/compare', {
    method: 'POST',
    body: JSON.stringify({ scenarios }),
  });
  if (!response.ok) {
    throw new Error('Failed to compare scenarios');
  }
  return response.json();
}

export async function chatWithAI(request: ChatRequest): Promise<ChatResponse> {
  const response = await fetchWithAuth('/insights/chat', {
    method: 'POST',
    body: JSON.stringify(request),
  });
  if (!response.ok) {
    throw new Error('Failed to chat with AI');
  }
  return response.json();
}

// Admin API Types
export interface UpdateSiteRequest {
  capacity?: CapacityRow[];
  costParameters?: CostParameters;
}

export interface UpdateSiteResponse {
  site: Site;
}

export interface AdminError {
  error: {
    code: string;
    message: string;
    details?: string[];
  };
}

// Admin API Functions
export async function updateSite(siteId: string, data: UpdateSiteRequest): Promise<UpdateSiteResponse> {
  const response = await fetchWithAuth(`/admin/sites/${siteId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const errorData = await response.json() as AdminError;
    throw new Error(errorData.error?.details?.join(', ') || errorData.error?.message || 'Failed to update site');
  }
  return response.json();
}

export interface CreateSiteRequest {
  name: string;
  location: string;
  capabilities: SiteCapabilities;
  costParameters: CostParameters;
  initialCapacity?: Array<{
    format: string;
    plannedCases: number;
    loadCapacity: number;
  }>;
}

export async function createSite(data: CreateSiteRequest): Promise<{ site: Site }> {
  const response = await fetchWithAuth('/admin/sites', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const errorData = await response.json() as AdminError;
    throw new Error(errorData.error?.details?.join(', ') || errorData.error?.message || 'Failed to create site');
  }
  return response.json();
}

// Export API Types
export interface ExportPDFRequest {
  siteId: string;
  includeScenario?: boolean;
  scenario?: {
    adjustments: FormatAdjustment[];
    metrics: ScenarioMetrics;
  };
}

// Export API Functions
export async function exportSitePDF(request: ExportPDFRequest): Promise<Blob> {
  const response = await fetchWithAuth('/export/pdf', {
    method: 'POST',
    body: JSON.stringify(request),
  });
  if (!response.ok) {
    throw new Error('Failed to generate PDF');
  }
  return response.blob();
}
